import {
  SYS_SPACE_FIXED_100,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_200,
} from '@oneaudi/unified-web-common';
import { ButtonGroup, Text, TextButton, TextLink } from '@oneaudi/unified-web-components';
import styled from 'styled-components';

export const StyledSubPageNavigation = styled.div<{
  isSticky: boolean;
  fascinationMode: boolean;
}>`
  --boxShadow: 0 5px 4px -4px rgba(0, 0, 0, 0.16);
  ${(props) => (props.isSticky ? `position: sticky; top: 0;` : '')};

  box-shadow: var(--boxShadow);
  z-index: 97;

  ${({ fascinationMode }) =>
    process.env.IS_LEGACY || !fascinationMode
      ? ''
      : `
    @keyframes moveIn {
      to {
        transform: translateY(0px);
        opacity: 1;
      }
    }

    @keyframes moveOut {
      to {
        transform: translateY(-100%);
        opacity: 0;
      }
    }

    visibiliy: hidden;
    opacity: 0;
    position: fixed;
    top: 0;
    width: 100%;
    max-width: var(--max-content-width);

    &.show {
      visibiliy: visible;
      opacity: 1;
      transform: translateY(-100%);
      animation: moveIn 600ms cubic-bezier(0.215, 0.61, 0.355, 1) forwards;
    }

    &.hide {
      visibiliy: visible;
      opacity: 1;
      transform: translateY(0px);
      animation: moveOut 600ms cubic-bezier(0.215, 0.61, 0.355, 1) forwards;
    }
  }
  `}
`;

/** **
 * MAINMENU
 *** */
export const StyledMainMenu = styled.nav`
  position: relative;
  padding-block: ${process.env.IS_LEGACY ? SYS_SPACE_FIXED_150 : 'var(--spacing-relative-sm)'};
  padding-inline: var(--page-margin);
  background: ${({ theme }) => theme.colors.mainMenu};
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg + 1}px) {
    padding-block: ${process.env.IS_LEGACY ? SYS_SPACE_FIXED_100 : 'var(--spacing-relative-sm)'};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}px) {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: max-content 1fr auto;
    gap: 28px;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }
`;

export const LeftContainer = styled.div`
  width: 100%;
  flex-shrink: 0;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    width: auto;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}px) {
    max-width: 200px;
  }
`;

export const RightContainer = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    display: flex;
    flex-wrap: wrap;
    gap: ${SYS_SPACE_FIXED_150};
    flex-shrink: 0;
  }
`;

export const SubmenuTriggerWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    height: 58px;
    width: auto;
    display: flex;
    align-items: center;
  }
`;
export const MenuTriggerSectionWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    height: 58px;
    width: auto;
    display: flex;
    align-items: center;
    margin-inline-end: 28px;
  }
`;

export const SubmenuTriggerButton = styled.button`
  text-align: left;
  border: none;
  background-color: transparent;
  font-family: inherit;
  padding: 0;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    align-items: normal;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.xl}px) {
    display: none;
  }
`;

export const ToggleButton = styled.button`
  text-align: left;
  border: none;
  background-color: transparent;
  font-family: inherit;
  padding: 0;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    align-items: normal;
  }
`;

/** **
 * SUBMENU - Desktop
 *** */
export const DesktopMenuContainer = styled.div<{
  isSticky: boolean;
}>`
  transition: ${({ theme }) => theme.animations.transition};
  transition-property: height;
  position: relative;
  margin: 0;
  z-index: 96;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg - 1}px) {
    display: none;
  }
`;

export const DesktopMenuWrapper = styled.div<{
  withCTA: boolean;
}>`
  width: 100%;
  height: var(--height, 0);
  overflow-y: auto;
  padding: 0;
  position: absolute;
  top: 0;
  z-index: 97;
  box-sizing: border-box;
  visibility: hidden;
  transition: ${({ theme }) => theme.animations.transition};
  transition-property: height, visibility;
  background-color: ${({ theme }) => theme.colors.secondLevelMenu};
  box-shadow: var(--boxShadow);

  &.show {
    visibility: visible;
  }
  .subpage-navigation-bar {
    margin-block: ${SYS_SPACE_FIXED_100};
  }
  padding-inline: calc(var(--page-margin) - ${SYS_SPACE_FIXED_200});

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}px) {
    display: none;
  }
`;

export const StyledButtonGroup = styled(ButtonGroup)`
  > div {
    width: 100%;
  }
  align-items: center;
`;

export const StyledParagraph = styled(Text)`
  color: ${({ theme }) => theme.colors.headline};
`;

export const StyledHeadline = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.xl - 1}px) {
    display: none;
  }
`;

export const StyledSubheadline = styled(Text)`
  color: ${({ theme }) => theme.colors.subheadline};
  display: block;
`;

export const SecondLevelMenu = styled.div<{
  isSticky: boolean;
}>`
  transition: ${({ theme }) => theme.animations.transition};
  ${({ theme }) => {
    if (process.env.IS_LEGACY) {
      return `background-color: ${theme.colors.secondLevelMenu}`;
    } else {
      return '';
    }
  }};
  transition-property: height;
  position: relative;
  margin: 0;
  z-index: 96;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg - 1}px) {
    display: none;
  }
`;
/** **
 * SUBMENU - Mobile menu
 *** */
export const MobileMenuContainer = styled.div<{ fascinationMode?: boolean }>`
  ${({ fascinationMode }) =>
    process.env.IS_LEGACY || !fascinationMode
      ? ''
      : `
        overflow-y: scroll;
        max-height: 90vh; /* fallback for dynamic viewport height - dvh */ 
        max-height: 90dvh;
      `};
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: none;
  }
`;

export const MobileMenuWrapper = styled.nav`
  height: var(--height, 0);
  transition: ${({ theme }) => theme.animations.transition};
  transition-property: height, visibility;
  visibility: hidden;
  overflow: hidden;
  z-index: 96;
  &.show {
    visibility: visible;
    overflow: auto;
  }
  background-color: ${({ theme }) => theme.colors.secondLevelMenu};
  box-shadow: var(--boxShadow);
  position: relative;
  z-index: 96;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: none;
  }
`;

export const LinkList = styled.ul`
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0;
  top: 100%;
  z-index: 97;
  width: 100vw;
  box-sizing: border-box;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: none;
  }
`;

export const LinkListItem = styled.li`
  padding-block: var(--spacing-relative-sm);
  margin-inline: var(--page-margin);

  &:first-child {
    margin-block-start: var(--spacing-relative-sm);
  }
  &:last-child {
    margin-block-end: ${SYS_SPACE_FIXED_150};
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    &:last-child {
      margin-block-end: var(--spacing-relative-md);
    }
  }
`;

export const LinkListLink = styled(TextLink)`
  text-decoration: none !important;
`;

export const UnderlinedText = styled.span`
  border-bottom: 2px solid ${({ theme }) => theme.colors.underline};
  padding-bottom: 4px;
  display: inline-block;
  margin-block-end: -6px;
`;

export const MobileMenuButtonWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    display: none;
  }
`;

export const MobileMenuButtonContainer = styled.div`
  max-width: 100%;
  display: grid;
  padding: 0;
  margin-bottom: var(--spacing-relative-xl);
  margin-inline: var(--page-margin);
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    display: none;
  }
`;

/** **
 * STICKY BUTTONS
 *** */
export const StyledStickyButtons = styled.div`
  background: ${({ theme }) => theme.colors.stickyButtonsBg};
  padding-inline: var(--page-margin);
  box-sizing: border-box;
  overflow: hidden;
  box-shadow: var(--boxShadow);

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    display: none;
  }
`;

export const StickyButtonsContainer = styled.div`
  margin: 0 0 16px 0;
  display: grid;
  gap: ${SYS_SPACE_FIXED_100};
`;

export const VtpButtonGroup = styled.div`
  gap: ${SYS_SPACE_FIXED_100};
  display: flex;
  flex-direction: column;
`;

export const MobileMenuButton = styled(TextButton)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    display: none;
    white-space: nowrap;
  }
`;

export const StyledTextButton = styled(TextButton)<{ isActive?: boolean }>`
  white-space: nowrap;
`;
